import React from 'react';
import styled from 'styled-components';

import Icon from 'components/commons/Icon';
import Flag from 'components/Flag';

import {
  colorPrimary,
  colorGray,
  colorWhite,
  colorBlack900,
  colorBlueLight,
  colorGrayLight,
  colorYellow,
  pSmall,
  mediaQueryMobileLargeMin,
  colorPrimaryLighten95,
  colorAccent,
  mediaQueryDesktopMin,
} from 'styles';

const miniFlagWidth = 24;
const flagWidth = 34;

export const CurrencyLabel = styled.span`
  margin-top: 2px;
  margin-right: calc(8px - (${flagWidth}px - ${miniFlagWidth}px) / 2);
  font-size: 14px;
  color: ${colorBlack900};
`;

export const DropdownToggle = styled.button`
  position: absolute;
  display: flex;
  align-items: center;
  right: 16px;
  background: ${colorWhite};
  border: 0;
  outline: 0;
  z-index: 3;
  top: calc(50% - (16px / ${miniFlagWidth / flagWidth}) / 2);

  &:after {
    content: '';
    position: absolute;
    top: -16px;
    bottom: -16px;
    left: -16px;
    right: -16px;
    z-index: 1;
  }

  & > svg {
    fill: ${colorGray};
    transform: rotate(0deg);
  }

  &:hover {
    & > ${CurrencyLabel} {
      color: ${colorPrimary};
    }

    & > svg {
      fill: ${colorPrimary};
    }
  }

  ${({ isOpen }) => {
    if (isOpen) {
      return `
        ${CurrencyLabel} {
          color: ${colorPrimary};
        }

        & > svg {
          fill: ${colorPrimary};
          transform: rotate(180deg);
        }
      `;
    }

    return '';
  }}
`;

export const DropdownListWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  min-width: 340px;
  max-width: 100%;
  background: ${colorWhite};
  z-index: 3000000000;
  overflow: auto;

  @media all and ${mediaQueryMobileLargeMin} {
    position: absolute;
    height: 376px;
    top: calc(50% + 16px);
    bottom: unset;
    box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.14);
  }
`;

export const DropdownListHeader = styled.div``;

export const DesktopSearchInputWrapper = styled.div`
  padding: 0 17px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${colorBlueLight};

  ${({ isFocused }) => {
    if (isFocused) {
      return `
        border-bottom: 1px solid ${colorPrimary};
      `;
    }

    return '';
  }}
`;

export const DesktopSearchInput = styled.input`
  color: ${colorBlack900};
  display: block;
  font-size: 16px;
  font-weight: 400;
  outline: none;
  padding: 12px 16px 12px 10px;
  border: 0;
  width: 100%;

  @media all and ${mediaQueryDesktopMin} {
    font-size: 14px;
  }

  &::placeholder {
    font-size: 16px;
    font-weight: 400;
    line-height: 1;

    @media all and ${mediaQueryDesktopMin} {
      font-size: 14px;
    }
  }
`;

export const ButtonGroupWrapper = styled.div`
  ${({ isFocused }) => {
    if (isFocused) {
      return `
        padding: 88px 16px 16px;
      `;
    }

    return 'padding: 16px;';
  }}
`;

export const DropdownList = styled.div`
  position: static;
  padding-top: ${({ isFocused, isMobile }) =>
    isFocused && isMobile ? '72px' : '0'};

  @media all and ${mediaQueryMobileLargeMin} {
    padding-top: 0;
  }
`;

export const SearchResultsList = styled.div`
  position: relative;
  background: ${colorWhite};
  padding-top: ${({ isFocused, isMobile }) =>
    isFocused && isMobile ? '72px' : '0'};

  &::before {
    content: '';
    position: absolute;
    height: 10px;
    width: 100%;
    top: 72px;
  }

  @media all and ${mediaQueryMobileLargeMin} {
    padding-top: 0;

    &::before {
      display: none;
    }

    margin-top: 0;
  }
`;

export const OptionsTitle = styled.div`
  ${pSmall}

  position: sticky;
  top: 48px;
  background: ${colorGrayLight};
  padding: 9px 16px 4px;

  @media all and ${mediaQueryMobileLargeMin} {
    position: static;
  }
`;

export const ContainerOptions = styled.div`
  position: relative;
`;

export const Option = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 18px;
  height: 48px;
  background: ${colorWhite};

  &:hover {
    background: rgba(242, 245, 248, 0.3);
  }

  ${({ isHidden }) => {
    if (isHidden) {
      return `
        opacity: 0.3;
        pointer-events: none;
      `;
    }

    return '';
  }}
`;

export const OptionButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: 0;
  outline: 0;
  flex: 1;
  height: 100%;
  padding: 12px 16px;
`;

export const OptionName = styled.span`
  ${pSmall}

  flex: 1;
  text-align: start;
`;

export const StarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  background: transparent;
  border: 0;
  outline: 0;
`;

export const StarIcon = styled(({ active, ...props }) => (
  <Icon name="star" {...props} />
))`
  stroke: ${colorGray};
  fill: ${colorWhite};
  cursor: pointer;

  &:hover {
    stroke: ${colorYellow};
  }

  ${({ active }) => {
    if (active) {
      return `
        stroke: ${colorYellow};
        fill: ${colorYellow};
      `;
    }

    return '';
  }}
`;

export const NoDataLabel = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  & > p {
    line-height: 16px;
  }
`;

export const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px auto;
`;

export const ListFlag = styled(Flag)`
  margin-right: 8px;
`;

export const MainFlag = styled(Flag)`
  margin-right: calc(8px - (${flagWidth}px - ${miniFlagWidth}px) / 2);
  transform: scale(${miniFlagWidth / flagWidth});
`;

export const ButtonToggle = styled.button`
  border: 1px solid #cacccb;
  border-radius: 4px;
  padding: 0 14px;
  background: #fff;
  position: relative;
  height: 32px;

  &:hover {
    background: ${colorPrimaryLighten95};
    border: 1px solid #76c1a6;
  }
`;

export const DropdownName = styled.span`
  display: block;
  font-size: 14px;
  line-height: 24px;
  color: ${colorAccent};
  cursor: pointer;
`;
